import { useBotUpdateCopyTradeMutation } from "@/api/bot/bot-update-copy-trade";
import { useCopyTradeWallets } from "@/hooks/use-copy-trade-data";
import { useWallet } from "@/providers/wallet";
import { Button } from "@/ui/button";
import GradientBorderRadius from "@/ui/gradient-border-radius";
import { IconComponent } from "@/ui/icon";
import Input from "@/ui/input";
import { Modal } from "@/ui/modals/modal";
import { isAddress } from "ethers";
import { useState } from "react";

// Solana address validator
const isSolanaAddress = (address: string) => {
  // Check if address is a string and has correct length
  if (typeof address !== "string" || address.length > 44) {
    return false;
  }
  // Check if address only contains base58 characters
  // Base58 alphabet: 123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz
  const base58Regex =
    /^[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]+$/;
  return base58Regex.test(address);
};

const AddWalletModal = ({ isConnected }: { isConnected: boolean }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [walletToAdd, setWalletToAdd] = useState("");
  const [validationError, setValidationError] = useState("");

  const { refreshWallets } = useCopyTradeWallets();
  const { userId, currentWallet, appPubKey } = useWallet();
  const { mutate: mutateUpdate } = useBotUpdateCopyTradeMutation();

  const onClose = () => {
    setIsOpen(false);
    setValidationError("");
    setWalletToAdd("");
  };

  const validateWallet = (address: string) => {
    if (!address) {
      setValidationError("Please enter a wallet address");
      return false;
    }

    // Check if walletToAdd is Eth valid address
    const isEth = isAddress(address);
    // Check if walletToAdd is Sol valid address
    const isSol = isSolanaAddress(address);

    if (!isEth && !isSol) {
      setValidationError("Invalid wallet address");
      return false;
    }

    // Check if the wrong type is used according to the current wallet
    if (isEth && currentWallet.botChainId !== 1) {
      setValidationError("Please use a Solana wallet address");
      return false;
    }

    if (isSol && currentWallet.botChainId !== 6517303) {
      setValidationError("Please use an Ethereum wallet address");
      return false;
    }

    setValidationError("");
    return true;
  };

  const onSubmit = async () => {
    if (!validateWallet(walletToAdd)) {
      return;
    }

    // Map form state to API parameters
    const apiParams = {
      chainId: currentWallet.botChainId,
      walletAddress: currentWallet.address,
      copyWallet: walletToAdd,
      userId: userId,
      isOn: false,
      nickname: "",
      isDelete: false,
      buyAmount: "1",
      buyAmountPercent: 1,
      appPubKey,
    };

    // Call the mutation with the new parameters
    await mutateUpdate(apiParams);
    setWalletToAdd("");
    setTimeout(() => {
      refreshWallets();
    }, 1000);
    setIsOpen(false);
  };

  return (
    <>
      <GradientBorderRadius
        gradient="default-border"
        radius="bigger"
        hover="r-100"
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-[6.25rem] h-[1.375rem] text-center text-[0.75rem] font-semibold flex items-center justify-center ${!isConnected ? "opacity-50 pointer-events-none " : ""}`}
        >
          + Add Wallet
        </button>
      </GradientBorderRadius>
      <Modal
        opened={isConnected && isOpen}
        onClose={onClose}
        className="w-[29.125rem] py-[0.9375rem] px-[0.6250rem]"
        size="29.125rem"
      >
        <Modal.Title title="Add Wallet" className="text-xs font-semibold" />
        <div className="w-full h-full">
          <div className="w-full grid grid-cols-[10%_1fr_1fr] gap-[1rem]">
            <div className="text-xs text-white font-semibold col-span-1 flex items-center">
              Wallet:
            </div>
            <div className="col-span-2 group">
              <Input
                leftSide={
                  <GradientBorderRadius
                    radius="right"
                    gradient={
                      walletToAdd && walletToAdd !== ""
                        ? "reversed-100"
                        : "default-border"
                    }
                    hover="input-50-left"
                    className="h-full w-[1.375rem]"
                  >
                    <div className="w-[1.375rem] h-full  flex items-center justify-center pl-[0.0625rem]">
                      <IconComponent
                        name="solanaLogo"
                        className="h-[1rem] w-[1rem]"
                      />
                    </div>
                  </GradientBorderRadius>
                }
                placeholder="Address"
                value={walletToAdd}
                onChange={(v) => setWalletToAdd(v)}
              />
              {validationError && (
                <p className="text-rose-700 text-xs mt-1">{validationError}</p>
              )}
            </div>
          </div>
        </div>
        <Modal.Footer>
          <div className="w-full flex flex-row gap-[0.3125rem] justify-center items-center">
            <Button
              title="Done"
              active={false}
              className="w-[9.4375rem] font-semibold text-xs"
              onClick={onSubmit}
            />
            <Button
              title="Clear"
              active={false}
              className="w-[9.4375rem] font-semibold text-xs"
              onClick={() => {
                setWalletToAdd("");
                setValidationError("");
              }}
            />
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddWalletModal;
